import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Button,
  FormControlLabel,
  Switch
} from '@mui/material';
import { styled } from '@mui/material/styles';
import WaitTimeDebug from '../debug/WaitTimeDebug';
import {
  DirectionsCar as VehiclesIcon,
  Person as DriversIcon,
  People as CustomersIcon,
  Route as RidesIcon,
  CheckCircle as CompletedIcon,
  Cancel as CanceledIcon,
  AccessTime as PendingIcon,
  LocalTaxi as TaxiIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  FlashOn as FlashOnIcon,
  Timer as WaitTimeIcon
} from '@mui/icons-material';
import brandingConfig from '../../config/brandingConfig';
import { Link } from 'react-router-dom';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import LiveMap from './LiveMap';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Statische Maps – einmalig erstellt, nie neu berechnet
const STATUS_LABEL_MAP = {
  'open': 'Angefragt',
  'accepted': 'Akzeptiert',
  'arrived': 'Angekommen',
  'pre_accepted': 'Vom Fahrer angenommen',
  'scheduled': 'Geplant',
  'in_progress': 'Zielfahrt',
  'completed': 'Abgeschlossen',
  'cancelled': 'Storniert',
  'cancelled_by_driver': 'Vom Fahrer storniert',
};

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  height: '100%',
  minHeight: '150px', // Minimale Höhe für einheitliches Aussehen
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StatIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: `${brandingConfig.colors.primary}30`, // 30 = 19% Transparenz
  marginBottom: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    fontSize: 32,
    color: brandingConfig.colors.primary,
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: theme.spacing(0.5),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const ListCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

// Verwende einen anderen Ansatz für RideStatusChip mit expliziten Funktionen
const getChipColorForStatus = (status, theme) => {
  switch (status) {
    case 'completed':
      return theme.palette.success.main;
    case 'cancelled':
    case 'cancelled_by_driver':
      return theme.palette.error.main;
    case 'in_progress':
      return theme.palette.primary.main;
    case 'arrived':
      return theme.palette.success.light;
    case 'accepted':
      return theme.palette.info.main;
    case 'open':
      return theme.palette.warning.main;
    default:
      return theme.palette.grey[500];
  }
};

// Memoized RideStatusChip mit React.memo
const RideStatusChip = React.memo(styled(Chip)(({ theme, status }) => ({
  color: 'white',
  backgroundColor: getChipColorForStatus(status, theme),
  '& .MuiChip-label': {
    fontSize: '0.75rem',
  },
})));

// Hilfsfunktion zum Berechnen der Online-Zeit
const calculateOnlineTime = (driver) => {
  
  // Überprüfen, ob der Fahrer lastLogin-Daten hat
  if (!driver.lastLogin) {
    return 'Online seit unbekannt';
  }
  
  try {
    // Firebase Timestamp zu Date konvertieren
    let lastLoginDate;
    if (driver.lastLogin.toDate) {
      // Firebase Timestamp
      lastLoginDate = driver.lastLogin.toDate();
    } else if (driver.lastLogin.seconds) {
      // Firebase Timestamp als Objekt
      lastLoginDate = new Date(driver.lastLogin.seconds * 1000);
    } else {
      // String oder andere Formate
      lastLoginDate = new Date(driver.lastLogin);
    }
    
    const now = new Date();
    const diffMs = now - lastLoginDate;
    
    // Negative Zeiten abfangen
    if (diffMs < 0) {
      return 'Gerade eingeloggt';
    }
    
    // Berechnung der Stunden und Minuten
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    // Formatieren der Ausgabe
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `Online seit ${days} Tag(en)`;
    } else if (hours > 0) {
      return `Online seit ${hours} Std. ${minutes} Min.`;
    } else if (minutes > 0) {
      return `Online seit ${minutes} Min.`;
    } else {
      return 'Gerade eingeloggt';
    }
  } catch (error) {
    console.error('Fehler bei Online-Zeit-Berechnung:', error, driver);
    return 'Online seit unbekannt';
  }
};

// Optimierte und memoizierte UI-Komponenten für das Dashboard

// Diese Komponente wird nicht mehr benötigt, da wir die StatsCard direkt verwenden
// Die betreffende Komponente wurde entfernt, um Konflikte zu vermeiden

const Dashboard = () => {
  // Add state for map visibility
  const [isMapVisible, setIsMapVisible] = useState(false);
  
  // Verwende neues AppData System (memoized selectors um Re-render zu vermeiden)
  const drivers = useSelector(state => state.appData.drivers);
  const driversLoading = useSelector(state => state.appData.isLoading.drivers);
  const driversError = useSelector(state => state.appData.error.drivers);
  
  const vehicles = useSelector(state => state.appData.vehicles);
  const vehiclesLoading = useSelector(state => state.appData.isLoading.vehicles);
  const vehiclesError = useSelector(state => state.appData.error.vehicles);
  
  const rides = useSelector(state => state.appData.rides);
  const ridesLoading = useSelector(state => state.appData.isLoading.rides);
  const ridesError = useSelector(state => state.appData.error.rides);
  
  const shifts = useSelector(state => state.appData.shifts);
  const shiftsLoading = useSelector(state => state.appData.isLoading.shifts);
  const shiftsError = useSelector(state => state.appData.error.shifts);
  
  const customers = useSelector(state => state.appData.customers);
  const customersLoading = useSelector(state => state.appData.isLoading.customers);
  const customersError = useSelector(state => state.appData.error.customers);
  
  // Firebase Wartezeiten direkt lesen (keine eigene Berechnung im Dashboard)
  const [waitTimes, setWaitTimes] = useState({
    general: "Berechnung...",
    kombi: "Berechnung...",
    minibus: "Berechnung..."
  });
  const [nextUnavailable, setNextUnavailable] = useState(null);

  // Firebase Listener für Wartezeiten
  useEffect(() => {
    const waitTimeDocRef = doc(db, 'waitTimes', 'current');
    
    const unsubscribe = onSnapshot(waitTimeDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setWaitTimes({
          general: data.general || "Berechnung...",
          kombi: data.kombi || "Berechnung...",
          minibus: data.minibus || "Berechnung..."
        });
        setNextUnavailable(data.nextUnavailable || null);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Dashboard: Firebase Wartezeiten empfangen:', {
            general: data.general,
            kombi: data.kombi,
            minibus: data.minibus
          });
        }
      } else {
        // Fallback wenn keine Daten vorhanden
        setWaitTimes({
          general: "Keine Daten",
          kombi: "Keine Daten",
          minibus: "Keine Daten"
        });
        setNextUnavailable(null);
      }
    }, (error) => {
      console.error('Dashboard: Fehler beim Laden der Wartezeiten aus Firebase:', error);
      setWaitTimes({
        general: "Fehler",
        kombi: "Fehler",
        minibus: "Fehler"
      });
    });

    return () => unsubscribe();
  }, []);
  
  const currentWaitTime = waitTimes.general;

  // Überprüfen, ob Daten vom Redux-Store geladen werden
  const isReduxLoading = driversLoading || vehiclesLoading || ridesLoading || shiftsLoading || customersLoading;
  const hasError = driversError || vehiclesError || ridesError || shiftsError || customersError;

  // Ladezustand – AppDataManager übernimmt alles
  const [isManualLoading, setIsManualLoading] = useState(true);
  const isLoading = isReduxLoading || isManualLoading;

  useEffect(() => {
    if (!isReduxLoading) {
      setIsManualLoading(false);
    }
  }, [isReduxLoading]);

  // Stats als useMemo – kein useEffect+setState-Umweg mehr, kein Extra-Render-Zyklus
  const stats = useMemo(() => {
    if (isLoading || hasError) return {
      totalDrivers: 0, availableDrivers: 0, onlineDrivers: 0, onlineCustomers: 0,
      totalVehicles: 0, activeVehicles: 0, availableVehicles: 0, inUseVehicles: 0,
      vehicleTypes: { pkw: 0, kombi: 0, kleinbus: 0 },
      ridesByStatus: {}, totalShiftsToday: 0, preOrdersToday: 0, totalPreOrders: 0,
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const DONE = ['completed', 'cancelled', 'cancelled_by_driver'];

    const ridesByStatus = rides.reduce((acc, ride) => {
      acc[ride.status] = (acc[ride.status] || 0) + 1;
      return acc;
    }, {});

    const vehicleTypes = vehicles.reduce((acc, vehicle) => {
      if (vehicle.vehicleType === 1) acc.pkw += 1;
      else if (vehicle.vehicleType === 2) acc.kombi += 1;
      else if (vehicle.vehicleType === 3) acc.kleinbus += 1;
      return acc;
    }, { pkw: 0, kombi: 0, kleinbus: 0 });

    const totalPreOrders = rides.filter(r => r.orderType === 1 && !DONE.includes(r.status)).length;
    const preOrdersToday = rides.filter(ride => {
      if (ride.orderType !== 1 || DONE.includes(ride.status) || !ride.scheduledTime) return false;
      const d = new Date(ride.scheduledTime);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === todayMs;
    }).length;

    return {
      totalDrivers: drivers.length,
      availableDrivers: drivers.filter(d => d.isAvailable).length,
      onlineDrivers: drivers.filter(d => d.isOnline).length,
      onlineCustomers: customers.filter(c => c.isOnline).length,
      totalVehicles: vehicles.length,
      activeVehicles: vehicles.length,
      availableVehicles: vehicles.filter(v => !v.inUse).length,
      inUseVehicles: vehicles.filter(v => v.inUse === true).length,
      vehicleTypes,
      ridesByStatus,
      totalShiftsToday: shifts?.drivers ? Object.keys(shifts.drivers).length : 0,
      preOrdersToday,
      totalPreOrders,
    };
  }, [drivers, vehicles, rides, shifts, customers, isLoading, hasError]);

  // Vorberechnete Listen für die Karten – vermeiden doppelte filter()-Aufrufe im JSX
  const activeRidesList = useMemo(() => rides.filter(ride =>
    (ride.orderType === 2 && ['open', 'accepted', 'in_progress', 'arrived'].includes(ride.status)) ||
    (ride.orderType === 1 && ['accepted', 'in_progress', 'arrived'].includes(ride.status))
  ).slice(0, 5), [rides]);

  const scheduledRidesList = useMemo(() => rides
    .filter(ride => ride.orderType === 1 && (ride.status === 'scheduled' || ride.status === 'pre_accepted'))
    .sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime))
    .slice(0, 5), [rides]);

  const onlineDriversList = useMemo(() => drivers
    .filter(d => d.isOnline)
    .sort((a, b) => {
      if (a.isAvailable !== b.isAvailable) return b.isAvailable - a.isAvailable;
      return (a.name || '').localeCompare(b.name || '');
    })
    .slice(0, 5), [drivers]);

  // Farbwahl für Wartezeit – nur neu berechnet wenn sich currentWaitTime ändert
  const waitTimeColor = useMemo(() => {
    if (currentWaitTime.includes("nicht verfügbar") ||
        currentWaitTime.includes("Keine Fahrer verfügbar") ||
        currentWaitTime.includes("30+")) return "error.main";
    if (currentWaitTime.includes("nach Ausfallzeit") ||
        currentWaitTime.startsWith("2") ||
        currentWaitTime.startsWith("3")) return "warning.main";
    return "success.main";
  }, [currentWaitTime]);

  const getRideIcon = useCallback((ride) => {
    if (ride.orderType === 1) return <EventIcon />;
    if (ride.orderType === 2) return <FlashOnIcon />;
    switch (ride.status) {
      case 'completed': return <CompletedIcon />;
      case 'cancelled': return <CanceledIcon />;
      case 'in_progress': return <TaxiIcon />;
      case 'arrived': return <DriversIcon />;
      case 'accepted':
      case 'pre_accepted': return <TaxiIcon />;
      default: return <PendingIcon />;
    }
  }, []);

  const formatDateTime = useCallback((dateInput) => {
    if (!dateInput) return 'N/A';
    try {
      let date;
      if (dateInput.toDate) {
        date = dateInput.toDate();
      } else if (dateInput.seconds) {
        date = new Date(dateInput.seconds * 1000);
      } else {
        date = new Date(dateInput);
      }
      if (isNaN(date.getTime())) return 'Ungültiges Datum';
      return DATE_FORMATTER.format(date);
    } catch (error) {
      console.error('Fehler beim Formatieren des Datums:', error, dateInput);
      return 'Fehler beim Formatieren';
    }
  }, []);

  const translateStatus = useCallback((status) => STATUS_LABEL_MAP[status] || status, []);

  const handleMapToggle = useCallback((e) => setIsMapVisible(e.target.checked), []);
  
  if (isLoading) {
    return <Loading message="Dashboard wird geladen..." />;
  }
  
  if (hasError) {
    console.error("Dashboard Fehler:", { 
      driversError, 
      vehiclesError, 
      ridesError, 
      shiftsError 
    });
    return (
      <ErrorMessage
        title="Fehler beim Laden der Dashboard-Daten"
        message={`Beim Laden der Dashboard-Daten ist ein Fehler aufgetreten. 
          Bitte stellen Sie sicher, dass Sie mit dem Internet verbunden sind und Firebase korrekt konfiguriert ist. 
          Details: 
          ${driversError ? `Fahrer: ${typeof driversError === 'object' ? driversError.message || JSON.stringify(driversError) : driversError}` : ''} 
          ${vehiclesError ? `Fahrzeuge: ${typeof vehiclesError === 'object' ? vehiclesError.message || JSON.stringify(vehiclesError) : vehiclesError}` : ''} 
          ${ridesError ? `Fahrten: ${typeof ridesError === 'object' ? ridesError.message || JSON.stringify(ridesError) : ridesError}` : ''} 
          ${shiftsError ? `Schichten: ${typeof shiftsError === 'object' ? shiftsError.message || JSON.stringify(shiftsError) : shiftsError}` : ''}`}
        onRetry={() => setIsManualLoading(true)}
      />
    );
  }
  
  return (
    <Box className="dashboard-container" sx={{ flexGrow: 1, p: 0, maxWidth: '100%', width: '100%' }}>
      <Box sx={{ px: 3, pt: 2 }}>
        <PageHeader
          title="Dashboard"
          description={`Willkommen im ${brandingConfig.appName} Admin-Portal. Hier finden Sie eine Echtzeit-Übersicht über die aktuellen Aktivitäten.`}
        />
      </Box>
      
      {/* Debug Panel - temporär deaktiviert für Performance */}
      {false && process.env.NODE_ENV === 'development' && (
        <WaitTimeDebug />
      )}
      
      {/* Stats Cards - Horizontal Scrollable */}
      <Box className="scrollable-container" sx={{ pt: 3 }}>
        <Box className="scrollable-cards">
          {/* Wartezeitkarte als erste Karte */}
          <StatsCard elevation={2}>
            <StatIcon>
              <WaitTimeIcon />
            </StatIcon>
            <StatValue
              sx={{ fontSize: '1.5rem', color: waitTimeColor }}
            >
              {currentWaitTime}
            </StatValue>
            <StatLabel>Aktuelle Wartezeit</StatLabel>
            {nextUnavailable && (
              <Typography 
                variant="caption" 
                color="error.main" 
                sx={{ 
                  fontSize: '0.65rem', 
                  display: 'block', 
                  mt: 0.5,
                  textAlign: 'center'
                }}
              >
                Keine Fahrer verfügbar in {nextUnavailable.minutesUntil} Min (für {nextUnavailable.duration} Min)
              </Typography>
            )}
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <DriversIcon />
            </StatIcon>
            <StatValue>{`${stats.onlineDrivers} / ${stats.totalDrivers}`}</StatValue>
            <StatLabel>Fahrer Online</StatLabel>
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <DriversIcon />
            </StatIcon>
            <StatValue>{`${stats.availableDrivers} / ${stats.totalDrivers}`}</StatValue>
            <StatLabel>Verfügbare Fahrer</StatLabel>
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <CustomersIcon />
            </StatIcon>
            <StatValue>{stats.onlineCustomers}</StatValue>
            <StatLabel>Kunden Online</StatLabel>
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <VehiclesIcon />
            </StatIcon>
            <StatValue>{`${stats.inUseVehicles || 0} / ${stats.totalVehicles || 0}`}</StatValue>
            <StatLabel>Aktive Fahrzeuge</StatLabel>
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <RidesIcon />
            </StatIcon>
            <StatValue>{(stats.ridesByStatus.open || 0) + 
                   (stats.ridesByStatus.accepted || 0) + 
                   (stats.ridesByStatus.in_progress || 0) + 
                   (stats.ridesByStatus.arrived || 0)}</StatValue>
            <StatLabel>Aktive Fahrten</StatLabel>
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <ScheduleIcon />
            </StatIcon>
            <StatValue>{stats.preOrdersToday}</StatValue>
            <StatLabel>Vorbestellungen heute</StatLabel>
          </StatsCard>
          
          <StatsCard elevation={2}>
            <StatIcon>
              <ScheduleIcon />
            </StatIcon>
            <StatValue>{stats.totalPreOrders}</StatValue>
            <StatLabel>Vorbestellungen Gesamt</StatLabel>
          </StatsCard>
        </Box>
      </Box>
      
      {/* Lists - Horizontal Scrollable */}
      <Box className="scrollable-container" sx={{ pt: 3, mb: 4 }}>
        <Box className="scrollable-cards">
          {/* Recent Rides */}
          <ListCard elevation={2} sx={{ width: 'calc(100vw - 32px)', minWidth: '300px', maxWidth: '450px' }}>
            <CardHeader
              title="Aktive Fahrten"
              subheader="Laufende und angenommene Fahrten"
              action={
                <Button
                  component={Link}
                  to="/fahrten"
                  color="primary"
                  size="small"
                >
                  Alle anzeigen
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List sx={{ width: '100%', p: 0 }}>
                {activeRidesList.length > 0 ? (
                  activeRidesList.map((ride) => (
                    <React.Fragment key={ride.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{ cursor: 'pointer' }}
                        component={Link}
                        to={`/fahrten/${ride.id}`}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: ride.orderType === 1 ? 'secondary.main' : 'primary.main' }}>
                            {getRideIcon(ride)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" component="span">
                              {(ride.start || ride.pickup_address)?.substring(0, 15)}... → {(ride.destination || ride.destination_address)?.substring(0, 15)}...
                            </Typography>
                          }
                          secondary={
                            <Typography component="span" variant="body2">
                              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box component="span">
                                  <Typography component="span" variant="body2" color="text.primary" display="block">
                                    {ride.orderType === 2 ? "Sofortbestellung" : "Vorbestellung"}: {formatDateTime(ride.timestamp || ride.request_time)}
                                  </Typography>
                                  {ride.driverId ? (
                                    <Typography component="span" variant="body2" display="block">
                                      Fahrer: {ride.driverName || ride.driver?.user?.name || 'Zugewiesen'}
                                    </Typography>
                                  ) : (
                                    <Typography component="span" variant="body2" display="block">
                                      Kein Fahrer zugewiesen
                                    </Typography>
                                  )}
                                </Box>
                                <RideStatusChip
                                  label={translateStatus(ride.status)}
                                  size="small"
                                  status={ride.status}
                                />
                              </Box>
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText 
                      primary="Keine aktiven Fahrten" 
                      secondary="Laufende Fahrten werden hier angezeigt" 
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </ListCard>
          
          {/* Available Drivers */}
          <ListCard elevation={2} sx={{ width: 'calc(100vw - 32px)', minWidth: '300px', maxWidth: '450px' }}>
            <CardHeader
              title="Fahrer Online"
              action={
                <Button
                  component={Link}
                  to="/fahrer"
                  color="primary"
                  size="small"
                >
                  Alle anzeigen
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List sx={{ width: '100%', p: 0 }}>
                {onlineDriversList.length > 0 ? (
                  onlineDriversList.map((driver) => (
                      <React.Fragment key={driver.id}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{ cursor: 'pointer' }}
                          component={Link}
                          to={`/fahrer/${driver.id}`}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {driver.name?.charAt(0) || 'U'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" component="span">
                                {driver.name}
                              </Typography>
                            }
                            secondary={
                              <Typography component="span" variant="body2">
                                <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box component="span">
                                    <Typography component="span" variant="body2" color="text.primary" display="block">
                                      {driver.car?.model || 'Kein Fahrzeug zugewiesen'}
                                    </Typography>
                                    <Typography component="span" variant="body2" color="text.secondary" display="block">
                                      {driver.isOnline ? calculateOnlineTime(driver) : 'Offline'}
                                    </Typography>
                                  </Box>
                                  <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                      label="Online" 
                                      size="small" 
                                      color="success" 
                                      variant="outlined"
                                    />
                                    <Chip 
                                      label={driver.isAvailable ? 'Verfügbar' : 'Besetzt'} 
                                      size="small" 
                                      color={driver.isAvailable ? 'primary' : 'error'} 
                                      variant="outlined"
                                    />
                                  </Box>
                                </Box>
                              </Typography>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </React.Fragment>
                    ))
                ) : (
                  <ListItem>
                    <ListItemText 
                      primary="Keine Fahrer online" 
                      secondary="Online-Fahrer werden hier angezeigt" 
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </ListCard>

          {/* Scheduled Rides */}
          <ListCard elevation={2} sx={{ width: 'calc(100vw - 32px)', minWidth: '300px', maxWidth: '450px' }}>
            <CardHeader
              title="Geplante Vorbestellungen"
              subheader="Noch nicht gestartete Vorbestellungen"
              action={
                <Button
                  component={Link}
                  to="/fahrten"
                  color="primary"
                  size="small"
                >
                  Alle anzeigen
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List sx={{ width: '100%', p: 0 }}>
                {scheduledRidesList.length > 0 ? (
                  scheduledRidesList.map((ride) => (
                      <React.Fragment key={ride.id}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{ cursor: 'pointer' }}
                          component={Link}
                          to={`/fahrten/${ride.id}`}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                              <EventIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" component="span">
                                {(ride.start || ride.pickup_address)?.substring(0, 15)}... → {(ride.destination || ride.destination_address)?.substring(0, 15)}...
                              </Typography>
                            }
                            secondary={
                              <Typography component="span" variant="body2">
                                <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box component="span">
                                    <Typography component="span" variant="body2" color="text.primary" display="block">
                                      Geplant für: {formatDateTime(ride.scheduledTime)}
                                    </Typography>
                                    {ride.driverId ? (
                                      <Typography component="span" variant="body2" display="block">
                                        Fahrer: {ride.driverName || 'Zugewiesen'}
                                      </Typography>
                                    ) : (
                                      <Typography component="span" variant="body2" display="block">
                                        Kein Fahrer zugewiesen
                                      </Typography>
                                    )}
                                  </Box>
                                  <RideStatusChip
                                    label={translateStatus(ride.status)}
                                    size="small"
                                    status={ride.status}
                                  />
                                </Box>
                              </Typography>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </React.Fragment>
                    ))
                ) : (
                  <ListItem>
                    <ListItemText 
                      primary="Keine Vorbestellungen" 
                      secondary="Vorbestellungen werden hier angezeigt" 
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </ListCard>
        </Box>
      </Box>
      
      {/* Live Map - Full Width */}
      <Box className="full-width-container" sx={{ width: '100vw', maxWidth: '100%', mt: 4, p: 0 }}>
        <Card elevation={3} sx={{ borderRadius: 0 }}>
          <CardContent sx={{ pb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                Live-Fahrerstandorte
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMapVisible}
                      onChange={handleMapToggle}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>Karte anzeigen</Box>
                    </Typography>
                  }
                  sx={{ mr: 1 }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
        {isMapVisible && <LiveMap />}
      </Box>
    </Box>
  );
};

export default Dashboard;